import { useState, useEffect, useCallback } from 'react'
import { useSsoToken } from './useSsoToken'
import { getClaim, getClaimFolderId, getClaimDocuments } from '../services/claimsApi'
import { getApiErrorMessage } from '../utils/errorMessages'
import type { ClaimResponse, DocumentInfo } from '@api-sdk'

// ─── Public types ────────────────────────────────────────────────────────────

export interface ApiSlice<T> {
  data: T | null
  loading: boolean
  error: string | null
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const initialSlice = <T>(): ApiSlice<T> => ({ data: null, loading: false, error: null })
const loadingSlice = <T>(): ApiSlice<T> => ({ data: null, loading: true, error: null })

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useClaimDetails(claimNumber: string | null) {
  const { getToken } = useSsoToken()

  const [claim, setClaim] = useState<ApiSlice<ClaimResponse>>(initialSlice)
  const [documents, setDocuments] = useState<ApiSlice<DocumentInfo[]>>({ data: [], loading: false, error: null })
  const [folderId, setFolderId] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const fetchDetails = useCallback(async () => {
    if (!claimNumber) {
      setClaim(initialSlice)
      setDocuments({ data: [], loading: false, error: null })
      setFolderId(null)
      setLastUpdated(null)
      return
    }

    setClaim(loadingSlice)
    setDocuments({ data: [], loading: false, error: null })
    setFolderId(null)
    setLastUpdated(null)

    let token: string
    try {
      token = await getToken()
    } catch (err: unknown) {
      console.error('[ClaimDetails] Failed to get token:', err)
      const msg = getApiErrorMessage(err, 'Authentication failed. Please try again.')
      setClaim({ data: null, loading: false, error: msg })
      return
    }

    getClaim(claimNumber, token)
      .then((data) => {
        setClaim({ data, loading: false, error: null })
        setLastUpdated(new Date())
      })
      .catch((err: unknown) => {
        console.error('[ClaimDetails] Failed to load claim:', err)
        setClaim({ data: null, loading: false, error: getApiErrorMessage(err, 'Failed to load claim details.') })
      })

    getClaimFolderId(claimNumber, token)
      .then((id) => {
        setFolderId(id)
        if (!id) return
        setDocuments({ data: [], loading: true, error: null })
        getClaimDocuments(id, token)
          .then((docs) => {
            setDocuments({ data: docs, loading: false, error: null })
          })
          .catch((err: unknown) => {
            console.error(`[ClaimDetails] Failed to load documents for claim ${claimNumber}`)
            setDocuments({ data: [], loading: false, error: getApiErrorMessage(err, 'Failed to load documents.') })
          })
      })
      .catch((err: unknown) => {
        console.error('[ClaimDetails] Failed to load folder ID:', err)
        setFolderId(null)
      })
  }, [claimNumber, getToken])

  useEffect(() => {
    fetchDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimNumber])

  return {
    claim,
    documents,
    folderId,
    lastUpdated,
    retry: fetchDetails,
  }
}
